'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { getCourseByIdService, getCourseListService, getLinkableCoursesService, searchTPLinkableService } from '@/services/httpServices/CourseServices';
import type {
  CourseByIdResponse,
  CourseListResponse,
  CourseListServiceConfig,
  GetCourseByIdServiceConfig,
  LinkableCoursesResponse,
  LinkableCoursesServiceConfig,
  SearchTPLinkableResponse,
  SearchTPLinkableServiceConfig,
} from '@/services/httpServices/CourseServices/types';

import { courseByIdKeys, courseLinkableKeys, courseListKeys, infiniteCourseListKeys, searchTPLinkableKeys } from './courseQueryKeys';
import { buildStandardPaginatedParams } from './createInfiniteQuery';
import { useQueryConfig } from './useQueryConfig';

export function useCourseQuery(params: CourseListServiceConfig, enabled: boolean = true) {
  const { isEnabled } = useQueryConfig();

  return useQuery<CourseListResponse>({
    queryKey: courseListKeys(params),
    queryFn: async () => getCourseListService(params),
    enabled: isEnabled && enabled,
  });
}

/**
 * Hook to fetch course list with infinite scrolling using cursor-based pagination.
 * Used by the kanban view so the board fetches pages as the user scrolls each
 * column to the bottom, instead of relying on client-side over-fetching.
 */
export function useInfiniteCourseQuery(
  initialParams: CourseListServiceConfig,
  enabled: boolean = true,
) {
  const { isEnabled } = useQueryConfig();

  return useInfiniteQuery<CourseListResponse>({
    queryKey: infiniteCourseListKeys(initialParams),
    queryFn: async ({ pageParam }) => {
      const paginationParams = buildStandardPaginatedParams(
        initialParams,
        pageParam ? String(pageParam) : undefined,
      );
      return getCourseListService(paginationParams);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage === true && lastPage.pageInfo.endCursor !== null) {
        return lastPage.pageInfo.endCursor;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pageInfo.hasPreviousPage === true && firstPage.pageInfo.startCursor !== null) {
        return firstPage.pageInfo.startCursor;
      }
      return undefined;
    },
    enabled: isEnabled && enabled,
  });
}

export function useCourseByIdQuery(
  params?: GetCourseByIdServiceConfig,
  enabled?: boolean,
) {
  const { isEnabled } = useQueryConfig();

  return useQuery<CourseByIdResponse>({
    queryKey: courseByIdKeys(params),
    queryFn: async () => {
      if (params === undefined) {
        throw new Error('Course params are required');
      }
      return getCourseByIdService(params);
    },
    enabled: isEnabled && (enabled ?? (params !== undefined && params.params?.params.id !== undefined && params.params?.params.id !== '')),
    refetchOnWindowFocus: 'always',
  });
}

export function useSearchTPLinkableQuery(params?: SearchTPLinkableServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useQuery<SearchTPLinkableResponse>({
    queryKey: searchTPLinkableKeys(params),
    queryFn: async () => {
      if (params === undefined) {
        throw new Error('Params are required');
      }
      return searchTPLinkableService(params);
    },
    enabled: isEnabled && params !== undefined,
  });
}

export function useLinkableCoursesQuery(params?: LinkableCoursesServiceConfig) {
  const { isEnabled } = useQueryConfig();

  return useQuery<LinkableCoursesResponse>({
    queryKey: courseLinkableKeys(params),
    queryFn: async () => {
      if (params === undefined) {
        throw new Error('Params are required');
      }
      return getLinkableCoursesService(params);
    },
    enabled: isEnabled && params !== undefined,
  });
}
